import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  ACCESS_LEVELS,
  atLeastLevel,
  capabilityLabelKey,
  domainDescriptionKey,
  domainLabelKey,
  groupLabelKey,
  levelDescriptionKey,
  levelLabelKey,
  riskLabelKey,
} from '@/shared/access';
import type { AccessDomain, AccessLevel, CapabilityGrant, SubjectType } from '@/shared/access';
import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { useAccessCatalogue, useAccessGroups, useSetSubjectGrants, useSubjectGrants } from '../../hooks';

/**
 * The access matrix — one subject's rights across every platform domain.
 *
 * <p>Twenty domains is too many for a flat list, so they are folded into the same six sections the sidebar
 * uses: an administrator looking for "who can refund an order" looks under Commerce, exactly where they
 * would look in the product itself.
 *
 * <p>Editing is local until saved. A matrix is a set of related decisions — raising Orders to EDIT usually
 * comes with raising Checkout — and autosaving each control would fire a change event per click, evict
 * every cached resolution, and make a half-finished intent briefly real.
 */
export function AccessMatrixPage() {
  const { t } = useTranslation('access');
  const [params, setParams] = useSearchParams();

  const subjectType = (params.get('subjectType') as SubjectType | null) ?? 'GROUP';
  const subjectId = params.get('subjectId') ?? undefined;

  const catalogue = useAccessCatalogue();
  const groups = useAccessGroups();
  const grants = useSubjectGrants(subjectType, subjectId);
  const save = useSetSubjectGrants();

  const [levels, setLevels] = useState<Record<string, AccessLevel>>({});
  const [capabilities, setCapabilities] = useState<CapabilityGrant[]>([]);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  // Seed the working copy when a DIFFERENT subject loads — not on every arrival of `grants.data`.
  //
  // Two things depend on that distinction. Switching groups must not carry the previous group's unsaved
  // edits across, or they would be saved onto the wrong subject. But a background refetch of the SAME
  // subject must not overwrite what the administrator is halfway through typing — re-seeding there would
  // silently discard their edits, and if the query returned a fresh object each time it would also loop.
  const subjectKey = grants.data ? `${subjectType}:${subjectId}` : null;
  if (subjectKey && subjectKey !== loadedFor) {
    setLoadedFor(subjectKey);
    setLevels(grants.data?.levels ?? {});
    setCapabilities(grants.data?.capabilities ?? []);
  }

  const dirty = useMemo(() => {
    if (!grants.data) return false;
    return (
      JSON.stringify(normalise(levels)) !== JSON.stringify(normalise(grants.data.levels)) ||
      JSON.stringify(sortGrants(capabilities)) !== JSON.stringify(sortGrants(grants.data.capabilities))
    );
  }, [levels, capabilities, grants.data]);

  const sections = useMemo(() => groupDomains(catalogue.data?.domains ?? []), [catalogue.data]);

  const selectedGroup = (groups.data ?? []).find((g) => g.id === subjectId);

  if (catalogue.isLoading || groups.isLoading) return <LoadingState label={t('matrix.loading')} />;
  if (catalogue.isError) {
    return (
      <ErrorState
        title={t('matrix.catalogueError')}
        message={catalogue.error?.message ?? ''}
        onRetry={() => void catalogue.refetch()}
      />
    );
  }

  const setLevel = (domainCode: string, level: AccessLevel) =>
    setLevels((prev) => ({ ...prev, [domainCode]: level }));

  /** Bulk row action: apply one level to every domain in a section. */
  const setSectionLevel = (domains: AccessDomain[], level: AccessLevel) =>
    setLevels((prev) => {
      const next = { ...prev };
      domains.forEach((d) => {
        next[d.code] = level;
      });
      return next;
    });

  const capabilityAllowed = (domainCode: string, capability: { code: string; minimumLevel: AccessLevel }) => {
    const explicit = capabilities.find(
      (c) => c.domainCode === domainCode && c.capabilityCode === capability.code,
    );
    if (explicit) return explicit.effect === 'ALLOW';
    return atLeastLevel(levels[domainCode], capability.minimumLevel);
  };

  const toggleCapability = (
    domainCode: string,
    capability: { code: string; minimumLevel: AccessLevel },
    granted: boolean,
  ) => {
    const byDefault = atLeastLevel(levels[domainCode], capability.minimumLevel);
    setCapabilities((prev) => {
      const without = prev.filter(
        (c) => !(c.domainCode === domainCode && c.capabilityCode === capability.code),
      );
      // Only store the exception. Persisting a grant that merely restates the level would silently pin the
      // capability, so lowering the level later would no longer take it away.
      if (granted === byDefault) return without;
      return [
        ...without,
        { domainCode, capabilityCode: capability.code, effect: granted ? 'ALLOW' : 'DENY' },
      ];
    });
  };

  return (
    <Stack spacing={2} sx={{ pb: 10 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          select
          size="small"
          label={t('matrix.subjectLabel')}
          value={subjectId ?? ''}
          onChange={(e) => setParams({ subjectType: 'GROUP', subjectId: e.target.value })}
          sx={{ minWidth: 280 }}
        >
          {(groups.data ?? []).map((group) => (
            <MenuItem key={group.id} value={group.id}>
              {group.name}
            </MenuItem>
          ))}
        </TextField>
        {selectedGroup && (
          <Chip
            size="small"
            variant="outlined"
            label={t('matrix.affectedUsers', { count: selectedGroup.memberCount })}
          />
        )}
      </Stack>

      {!subjectId ? (
        <EmptyState title={t('matrix.noSubject.title')} description={t('matrix.noSubject.description')} />
      ) : grants.isLoading ? (
        <LoadingState label={t('matrix.loading')} />
      ) : grants.isError ? (
        <ErrorState
          title={t('matrix.grantsError')}
          message={grants.error?.message ?? ''}
          onRetry={() => void grants.refetch()}
        />
      ) : (
        <>
          {save.isError && <Alert severity="error">{save.error?.message}</Alert>}
          {sections.map(([group, domains]) => (
            <Paper key={group} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2">{t(groupLabelKey(group), group)}</Typography>
                <Box sx={{ flex: 1 }} />
                <Tooltip title={t('matrix.bulkHint')}>
                  <Box>
                    {ACCESS_LEVELS.map((level) => (
                      <Button
                        key={level}
                        size="small"
                        onClick={() => setSectionLevel(domains, level)}
                        sx={{ minWidth: 0 }}
                      >
                        {t(levelLabelKey(level))}
                      </Button>
                    ))}
                  </Box>
                </Tooltip>
              </Stack>

              <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
                {domains.map((domain) => (
                  <DomainRow
                    key={domain.code}
                    domain={domain}
                    level={levels[domain.code] ?? 'NONE'}
                    onLevelChange={(level) => setLevel(domain.code, level)}
                    isCapabilityGranted={(capability) => capabilityAllowed(domain.code, capability)}
                    onCapabilityToggle={(capability, granted) =>
                      toggleCapability(domain.code, capability, granted)
                    }
                  />
                ))}
              </Stack>
            </Paper>
          ))}

          {dirty && (
            <Paper
              elevation={8}
              sx={{
                position: 'sticky',
                bottom: 16,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                zIndex: 2,
              }}
            >
              <Typography variant="body2">
                {t('matrix.unsaved', { count: selectedGroup?.memberCount ?? 0 })}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button
                onClick={() => {
                  setLevels(grants.data?.levels ?? {});
                  setCapabilities(grants.data?.capabilities ?? []);
                }}
              >
                {t('common:action.cancel')}
              </Button>
              <Button
                variant="contained"
                disabled={save.isPending}
                onClick={() =>
                  save.mutate({
                    subjectType,
                    subjectId: subjectId as string,
                    body: { levels: normalise(levels), capabilities },
                  })
                }
              >
                {t('matrix.save')}
              </Button>
            </Paper>
          )}
        </>
      )}
    </Stack>
  );
}

interface DomainRowProps {
  domain: AccessDomain;
  level: AccessLevel;
  onLevelChange: (level: AccessLevel) => void;
  isCapabilityGranted: (capability: { code: string; minimumLevel: AccessLevel }) => boolean;
  onCapabilityToggle: (
    capability: { code: string; minimumLevel: AccessLevel },
    granted: boolean,
  ) => void;
}

/**
 * One domain: the four-level selector, with its capabilities behind a disclosure.
 *
 * <p>The disclosure is its own small button rather than an accordion wrapping the whole row. An accordion
 * header IS a button, and nesting the level toggles inside one makes them unreachable by keyboard and
 * turns every level change into an accidental collapse. Separating the two keeps both operable.
 */
function DomainRow({
  domain,
  level,
  onLevelChange,
  isCapabilityGranted,
  onCapabilityToggle,
}: DomainRowProps) {
  const { t } = useTranslation('access');
  const [open, setOpen] = useState(false);
  const hasCapabilities = domain.capabilities.length > 0;
  const domainLabel = t(domainLabelKey(domain.code), domain.code);

  return (
    <Box sx={{ py: 1.5 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <Box sx={{ minWidth: 220 }}>
          <Typography variant="body2" fontWeight={600}>
            {domainLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t(domainDescriptionKey(domain.code), '')}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />

        <ToggleButtonGroup
          exclusive
          size="small"
          value={level}
          onChange={(_, next: AccessLevel | null) => next && onLevelChange(next)}
          aria-label={domainLabel}
        >
          {ACCESS_LEVELS.map((option) => (
            // `describeChild` matters here: without it MUI puts the tooltip text in `aria-label`, which
            // REPLACES the button's own name — a screen-reader user hears "Read and change the data" with
            // no idea it is the Edit button. With it the text becomes `aria-describedby`, so the name
            // stays "Edit" and the explanation is additional rather than instead.
            <Tooltip key={option} title={t(levelDescriptionKey(option))} describeChild>
              <ToggleButton value={option} sx={{ px: 1.5, textTransform: 'none' }}>
                {t(levelLabelKey(option))}
              </ToggleButton>
            </Tooltip>
          ))}
        </ToggleButtonGroup>

        {hasCapabilities && (
          <Button
            size="small"
            onClick={() => setOpen((previous) => !previous)}
            endIcon={
              <ExpandMoreRoundedIcon
                sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s' }}
              />
            }
            aria-expanded={open}
          >
            {t('matrix.capabilities', { count: domain.capabilities.length })}
          </Button>
        )}
      </Stack>

      {/*
        Rendered conditionally rather than with `Collapse`. Collapse keeps the panel mounted but
        `visibility: hidden` until its transition finishes, so between the click and the end of the
        animation the switches are in the DOM yet unreachable to assistive technology. For a settings-row
        disclosure the animation is not worth a window in which the controls exist but cannot be operated.
      */}
      {hasCapabilities && open && (
        <Box>
          <Stack spacing={1} sx={{ pt: 1.5, pl: { md: 2 } }}>
            <Typography variant="caption" color="text.secondary">
              {t('matrix.capabilitiesHint')}
            </Typography>
            {domain.capabilities.map((capability) => {
              const granted = isCapabilityGranted(capability);
              const byDefault = atLeastLevel(level, capability.minimumLevel);
              return (
                <Stack key={capability.code} direction="row" spacing={1} alignItems="center">
                  <Switch
                    size="small"
                    checked={granted}
                    onChange={(e) => onCapabilityToggle(capability, e.target.checked)}
                    slotProps={{
                      input: {
                        'aria-label': t(capabilityLabelKey(capability.code), capability.code),
                      },
                    }}
                  />
                  <Typography variant="body2">
                    {t(capabilityLabelKey(capability.code), capability.code)}
                  </Typography>
                  <Tooltip title={t(riskLabelKey(capability.risk), capability.risk)} describeChild>
                    <WarningAmberRoundedIcon fontSize="inherit" color="warning" />
                  </Tooltip>
                  {granted !== byDefault && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={granted ? 'info' : 'warning'}
                      label={granted ? t('matrix.explicitAllow') : t('matrix.explicitDeny')}
                    />
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

/** Drops `NONE` entries — the API treats an absent domain as no access, so storing it is noise. */
function normalise(levels: Record<string, AccessLevel>): Record<string, AccessLevel> {
  return Object.fromEntries(
    Object.entries(levels)
      .filter(([, level]) => level !== 'NONE')
      .sort(([a], [b]) => a.localeCompare(b)),
  );
}

function sortGrants(grants: CapabilityGrant[]): CapabilityGrant[] {
  return [...grants].sort((a, b) =>
    `${a.domainCode}.${a.capabilityCode}`.localeCompare(`${b.domainCode}.${b.capabilityCode}`),
  );
}

/** Folds the flat catalogue into the sidebar's sections, preserving the catalogue's own domain order. */
function groupDomains(domains: AccessDomain[]): [string, AccessDomain[]][] {
  const sections = new Map<string, AccessDomain[]>();
  domains.forEach((domain) => {
    const bucket = sections.get(domain.group);
    if (bucket) bucket.push(domain);
    else sections.set(domain.group, [domain]);
  });
  return [...sections.entries()];
}
